import { api, cacheMinTimer, thunks } from "@app/api";
import { selectDeployments } from "@app/deployment";
import type { RollbackTarget } from "@app/deployment";
import { call, createSelector, select, waitForBootupComplete } from "@app/fx";
import { defaultEntity, extractIdFromLink } from "@app/hal";
import { type WebState, schema } from "@app/schema";
import { findSourceById, selectSources } from "@app/source";
import { normalizeGitRef, normalizeStr } from "@app/string-utils";
import type {
  DeployApp,
  DeployAppConfigEnv,
  DeployOperation,
  DeployService,
  DeployServiceResponse,
  Deployment,
  LinkResponse,
  ProvisionableStatus,
} from "@app/types";
import {
  findEnvById,
  hasDeployEnvironment,
  selectEnvironments,
  selectEnvironmentsByOrg,
} from "../environment";
import type { DeployImageResponse } from "../image";
import { type DeployOperationResponse, waitForOperation } from "../operation";
import { selectServiceById } from "../service";
import { findStackById, selectStacks } from "../stack";

export * from "./utils";

export interface DeployAppResponse {
  id: number;
  handle: string;
  git_repo: string;
  created_at: string;
  deleted_at: string;
  updated_at: string;
  deployment_method: string;
  status: ProvisionableStatus;
  _links: {
    account: LinkResponse;
    current_configuration: LinkResponse;
    current_deployment: LinkResponse;
    current_image: LinkResponse;
    current_source: LinkResponse;
    current_setting?: LinkResponse;
    maintenance_window?: LinkResponse;
  };
  _embedded: {
    // TODO: fill in
    services: DeployServiceResponse[] | null;
    current_image: DeployImageResponse | null;
    last_deploy_operation: DeployOperationResponse | null;
    last_operation: DeployOperationResponse | null;
  };
  _type: "app";
}

export const defaultAppResponse = (
  p: Partial<DeployAppResponse> = {},
): DeployAppResponse => {
  const now = new Date().toISOString();
  return {
    id: 1,
    handle: "",
    git_repo: "",
    created_at: now,
    deleted_at: "",
    updated_at: now,
    deployment_method: "",
    status: "provisioned",
    _links: {
      account: { href: "" },
      current_configuration: { href: "" },
      current_deployment: { href: "" },
      current_image: { href: "" },
      current_source: { href: "" },
      current_setting: { href: "" },
      maintenance_window: { href: "" },
      ...p._links,
    },
    _embedded: {
      services: [],
      current_image: null,
      last_deploy_operation: null,
      last_operation: null,
      ...p._embedded,
    },
    ...p,
    _type: "app",
  };
};

export const deserializeDeployApp = (payload: DeployAppResponse): DeployApp => {
  const links = payload._links;

  return {
    id: `${payload.id}`,
    handle: payload.handle,
    gitRepo: payload.git_repo,
    createdAt: payload.created_at,
    deletedAt: payload.deleted_at,
    updatedAt: payload.updated_at,
    deploymentMethod: payload.deployment_method,
    status: payload.status,
    environmentId: extractIdFromLink(links.account),
    currentConfigurationId: extractIdFromLink(links.current_configuration),
    currentDeploymentId: extractIdFromLink(links.current_deployment),
    currentImageId: extractIdFromLink(links.current_image),
    currentSourceId: extractIdFromLink(links.current_source),
    currentSettingId: extractIdFromLink(links.current_setting ?? { href: "" }),
    maintenanceWindowId: extractIdFromLink(
      links.maintenance_window ?? { href: "" },
    ),
  };
};

export const hasDeployApp = (a: DeployApp) => a.id !== "";
export const selectAppById = schema.apps.selectById;
export const selectApps = schema.apps.selectTable;
export const selectAppsAsList = schema.apps.selectTableAsList;
export const findAppById = schema.apps.findById;

export interface DeployAppRow extends DeployApp {
  envHandle: string;
  currentDeployment: Deployment;
  gitRef: string;
  gitCommitSha: string;
  dockerImageName: string;
  lastOperation: DeployOperation;
  lastDeployed: string;
  cost: number;
  totalCPU: number;
  totalMemoryLimit: number;
  totalServices: number;
  services: DeployService[];
}

export const selectAppsByEnvId = createSelector(
  selectAppsAsList,
  (_: WebState, props: { envId: string }) => props.envId,
  (apps, envId) => {
    if (envId === "") {
      return apps;
    }

    return apps.filter((app) => app.environmentId === envId);
  },
);

export const selectFirstAppByEnvId = createSelector(
  selectAppsByEnvId,
  (apps) => apps[0] || schema.apps.empty,
);

export const selectAppsByOrgAsList = createSelector(
  selectAppsAsList,
  selectEnvironmentsByOrg,
  (apps, envs) => {
    return apps.filter((app) => {
      const env = findEnvById(envs, { id: app.environmentId });
      return hasDeployEnvironment(env);
    });
  },
);

export const selectAppsByOrgAsListWithDeployments = createSelector(
  selectAppsByOrgAsList,
  selectDeployments,
  (apps, deployments) => {
    return apps.map((app) => {
      const deployment = schema.deployments.findById(deployments, {
        id: app.currentDeploymentId,
      });
      return { app, deployment };
    });
  },
);

export const selectLatestAppDeployment = createSelector(
  selectAppsByOrgAsListWithDeployments,
  (appsWithDeployments): { app: DeployApp; deployment: Deployment } | null => {
    if (!appsWithDeployments || appsWithDeployments.length === 0) return null;

    // Filter to only apps with valid deployments
    const validAppsWithDeployments = appsWithDeployments.filter(
      ({ deployment }) => {
        return (
          deployment && deployment.id !== "" && deployment.createdAt !== ""
        );
      },
    );

    if (validAppsWithDeployments.length === 0) return null;

    // Sort by deployment createdAt descending (latest first)
    // ISO 8601 dates are lexicographically sortable, so string comparison works
    const sorted = validAppsWithDeployments.sort((a, b) => {
      const aDate = a.deployment.createdAt;
      const bDate = b.deployment.createdAt;
      // Compare lexicographically (ISO dates sort correctly as strings)
      if (aDate < bDate) return 1;
      if (aDate > bDate) return -1;
      return 0;
    });

    // Return the app and deployment
    return sorted[0];
  },
);

export const selectAppsByEnvOnboarding = createSelector(
  selectEnvironments,
  selectAppsByOrgAsList,
  (envs, apps) => {
    return apps.filter((app) => {
      const env = findEnvById(envs, { id: app.environmentId });
      if (!hasDeployEnvironment(env)) {
        return false;
      }
      if (env.onboardingStatus === "unknown") {
        return false;
      }

      return true;
    });
  },
);

export const selectAppsByStack = createSelector(
  selectAppsAsList,
  selectEnvironments,
  (_: WebState, p: { stackId: string }) => p.stackId,
  (apps, envs, stackId) => {
    return apps.filter((app) => {
      const env = findEnvById(envs, { id: app.environmentId });
      return env.stackId === stackId;
    });
  },
);

export const selectAppsBySource = createSelector(
  selectAppsAsList,
  selectSources,
  (_: WebState, p: { sourceId: string }) => p.sourceId,
  (apps, sources, sourceId) => {
    return apps.filter((app) => {
      const source = findSourceById(sources, { id: app.currentSourceId });
      return source.id === sourceId;
    });
  },
);

export const selectAppsCountBySource = createSelector(
  selectAppsBySource,
  (apps) => apps.length,
);

export type AppDeploymentType =
  | "companion-repo"
  | "git-based"
  | "image-based"
  | "unknown";

// Helper selectors to resolve current deployment/image/config for an App
const selectCurrentDeploymentByAppId = createSelector(
  schema.deployments.selectTable,
  selectAppById,
  (deployments, app) => {
    return schema.deployments.findById(deployments, {
      id: app.currentDeploymentId,
    });
  },
);

const selectCurrentImageByAppId = createSelector(
  schema.images.selectTable,
  selectAppById,
  (images, app) => {
    return schema.images.findById(images, { id: app.currentImageId });
  },
);

const selectCurrentAppConfigByAppId = createSelector(
  schema.appConfigs.selectTable,
  selectAppById,
  (configs, app) => {
    return schema.appConfigs.findById(configs, {
      id: app.currentConfigurationId,
    });
  },
);

export const selectAppDeploymentType = createSelector(
  selectAppById,
  selectCurrentDeploymentByAppId,
  selectCurrentImageByAppId,
  selectCurrentAppConfigByAppId,
  (_app, deployment, image, config): AppDeploymentType => {
    const operationRef = normalizeGitRef(deployment.gitRef);
    const operationImage = normalizeStr(deployment.dockerImage);

    const currentRef = normalizeGitRef(image.gitRef);
    const currentImage = normalizeStr(config.env?.APTIBLE_DOCKER_IMAGE);

    const effectiveRef = operationRef || currentRef;
    const effectiveImage = operationImage || currentImage;

    if (effectiveRef && effectiveImage) return "companion-repo";
    if (effectiveRef) return "git-based";
    if (effectiveImage) return "image-based";
    return "unknown";
  },
);

export const fetchApps = api.get(
  "/apps?per_page=5000&no_embed=true",
  {
    supervisor: cacheMinTimer(),
  },
  function* (ctx, next) {
    yield* next();
    if (!ctx.json.ok) {
      return;
    }

    yield* schema.update(schema.apps.reset());
  },
);

export const selectEnvironmentByAppId = createSelector(
  selectEnvironments,
  selectAppById,
  (envs, app) => {
    return findEnvById(envs, { id: app.environmentId });
  },
);

export const selectStackByAppId = createSelector(
  selectStacks,
  selectEnvironmentByAppId,
  (stacks, env) => {
    return findStackById(stacks, { id: env.stackId });
  },
);

export const selectAppByServiceId = createSelector(
  selectServiceById,
  selectApps,
  (service, apps) => {
    return findAppById(apps, { id: service.appId });
  },
);

interface AppIdProp {
  id: string;
  withDeleted?: boolean;
  waitForBootup?: boolean;
}

export const fetchApp = api.get<AppIdProp, DeployAppResponse>(
  "/apps/:id",
  function* (ctx, next) {
    if (ctx.payload.waitForBootup) {
      yield* waitForBootupComplete();
    }

    if (ctx.payload.withDeleted) {
      const baseUrl = ctx.req().url;
      const url = new URL(baseUrl);
      url.searchParams.set("with_deleted", "true");
      ctx.request = ctx.req({ url: url.toString() });
    }

    yield* next();
  },
);

interface CreateAppProps {
  name: string;
  envId: string;
}

export const createDeployApp = api.post<CreateAppProps, DeployAppResponse>(
  "/accounts/:envId/apps",
  function* (ctx, next) {
    const { name, envId } = ctx.payload;
    const body = {
      handle: name,
      account_id: envId,
    };
    ctx.request = ctx.req({
      body: JSON.stringify(body),
    });

    yield* next();

    if (!ctx.json.ok) {
      return;
    }

    ctx.loader = { meta: { appId: ctx.json.value.id } };
  },
);

interface ScanAppOpProps {
  type: "scan_code";
  envId: string;
  appId: string;
  gitRef: string;
}
interface DeployAppOpProps {
  type: "deploy";
  envId: string;
  appId: string;
  gitRef: string;
}
interface ConfigAppOpProps {
  type: "configure";
  appId: string;
  env: DeployAppConfigEnv;
}

interface DeprovisionAppOpProps {
  type: "deprovision";
  appId: string;
}

type AppOpProps =
  | ScanAppOpProps
  | DeployAppOpProps
  | DeprovisionAppOpProps
  | ConfigAppOpProps;
export const createAppOperation = api.post<AppOpProps, DeployOperationResponse>(
  "/apps/:appId/operations",
  function* (ctx, next) {
    const { type } = ctx.payload;

    const getBody = () => {
      switch (type) {
        case "deprovision": {
          return { type };
        }

        case "deploy":
        case "scan_code": {
          const { gitRef } = ctx.payload;
          return { type, git_ref: gitRef };
        }

        case "configure": {
          const { env } = ctx.payload;
          return { type, env };
        }

        default:
          return {};
      }
    };

    const body = getBody();
    ctx.request = ctx.req({ body: JSON.stringify(body) });
    yield* next();
  },
);

export const appEntities = {
  app: defaultEntity({
    id: "app",
    deserialize: deserializeDeployApp,
    save: schema.apps.add,
  }),
};

export const restartApp = api.post<{ id: string }, DeployOperationResponse>(
  ["/apps/:id/operations", "restart"],
  function* (ctx, next) {
    const { id } = ctx.payload;
    const body = {
      type: "restart",
      id,
    };

    ctx.request = ctx.req({ body: JSON.stringify(body) });
    yield* next();

    if (!ctx.json.ok) {
      return;
    }

    const opId = ctx.json.value.id;
    ctx.loader = {
      message: `Restart app operation queued (operation ID: ${opId})`,
      meta: { opId: `${opId}` },
    };
  },
);

export const deployAppImage = api.post<
  {
    appId: string;
    dockerImage: string;
    registryUsername: string;
    registryPassword: string;
  },
  DeployOperationResponse
>(["/apps/:appId/operations", "deploy"], function* (ctx, next) {
  const { dockerImage, registryUsername, registryPassword } = ctx.payload;
  ctx.request = ctx.req({
    body: JSON.stringify({
      type: "deploy",
      settings: {
        APTIBLE_DOCKER_IMAGE: dockerImage,
      },
      sensitive_settings: {
        APTIBLE_PRIVATE_REGISTRY_USERNAME: registryUsername,
        APTIBLE_PRIVATE_REGISTRY_PASSWORD: registryPassword,
      },
    }),
  });
  yield* next();
});

// rollbackApp issues a "deploy" op pinned to a previous image by id, so it
// reuses that exact build instead of re-building/re-scanning from a git ref
// — optionally carrying an `env` patch to restore historical config alongside
// it. `env` keys are applied as-is by the API — callers are responsible for
// pre-blanking any keys that should be removed (see `envForRollback`). Build
// `target` with `rollbackTargetFor` (src/deployment) rather than constructing
// it by hand.
export const rollbackApp = api.post<
  {
    appId: string;
    target: RollbackTarget;
    env?: DeployAppConfigEnv;
  },
  DeployOperationResponse
>(["/apps/:appId/operations", "rollback"], function* (ctx, next) {
  const { target, env } = ctx.payload;

  ctx.request = ctx.req({
    body: JSON.stringify({
      type: "deploy",
      deploy_image_id: target.imageId,
      ...(env ? { env } : {}),
    }),
  });
  yield* next();

  if (!ctx.json.ok) {
    return;
  }

  const opId = ctx.json.value.id;
  ctx.loader = {
    message: `Rollback operation queued (operation ID: ${opId})`,
    meta: { opId: `${opId}` },
  };
});

export const deprovisionApp = thunks.create<{
  appId: string;
}>("deprovision-app", function* (ctx, next) {
  const { appId } = ctx.payload;
  yield* select((s: WebState) => selectAppById(s, { id: appId }));

  const deprovisionCtx = yield* call(() =>
    createAppOperation.run(
      createAppOperation({
        type: "deprovision",
        appId,
      }),
    ),
  );

  if (!deprovisionCtx.json.ok) {
    return;
  }
  const id = `${deprovisionCtx.json.value.id}`;
  yield* call(() => waitForOperation({ id }));
  yield* next();
});

interface UpdateApp {
  id: string;
  handle: string;
}

export const updateApp = api.put<UpdateApp>("/apps/:id", function* (ctx, next) {
  const { handle } = ctx.payload;
  const body = {
    handle,
  };
  ctx.request = ctx.req({ body: JSON.stringify(body) });

  yield* next();

  if (!ctx.json.ok) {
    return;
  }

  ctx.loader = {
    message: "Saved changes successfully!",
  };
});

export const appToOption = (
  app: DeployApp,
): { key: string; label: string; value: string } => {
  return {
    key: app.id,
    label: app.handle,
    value: app.id,
  };
};
